import getMailsRemovingStatus from "../../helpers/getMailsRemovingStatus";
import getCustomStatusColor from "../../helpers/getCustomStatusColor";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  type SearchParamsI,
  removeLabel,
  removeMailFromState,
  setAbortController,
  setApiEnable,
  setCurrentPage,
  setFormList,
  setFtpEnable,
  setIsFetching,
  setIsFetchingSideInboxFiles,
  setLabel,
  setMailData,
  setMailDataSecurity,
  setMailsCount,
  setMailsData,
  setMailsQuantity,
  setSelectedMailIds,
  setSideInboxFiles,
  setSubUserList,
  SubUserSentMailsT,
} from "../slices/inboxSlice";
import {
  CreateLabelApiRequest,
  DeleteLabelApiRequest,
  GetMailLabelsApiRequest,
  LabelMailsApiRequest,
  ReactGetSentMailsApiRequest,
  ReactGetSentMailsCountApiRequest,
  ReactGetSideInboxFilesApiRequest,
  ReactRemoveInboxMailsApiRequest,
  ReactUpdatePaymentSettingsApiRequest,
  ReactUpdateSecuritySettingsApiRequest,
  RemoveMailFromTrashApiRequest,
  UnlabelMailsApiRequest,
  UpdateLabelApiRequest,
} from "../../api/MailsController";
import { type RootState } from "../store";
import type { MailDataI } from "../../models/models";
import { GetSubUserDetailsApiRequest } from "../../api/SubUsersController";
import { GetAllEmbedDynamicFormsNamesByUserIdApiRequest } from "../../api/EmbedDynamicsController";

export const fetchInboxData = createAsyncThunk(
  "inbox/fetchInboxData",
  async (userId: string, { getState, dispatch }) => {
    if (!userId) {
      return;
    }

    dispatch(setIsFetching(true));

    const state = (getState() as RootState).inbox;

    // -------------- cancel previous request if exists --------------------

    state.abortController?.abort?.();

    // and create new controller for current request
    const abortController = new AbortController();

    dispatch(setAbortController(abortController));

    // ---------------------------------------------------------------------

    const tParam = state.searchParams.t;
    const searchParams = state.searchParams[tParam] as SearchParamsI;

    const payload = {
      filterGlobal: state.searchParams.t,
      filterStatus: state.searchParams.f,
      searchQuery: searchParams?.q || "",
      sorting: {
        by: state.searchParams.sortBy,
        asc: state.searchParams.isSortAsc,
      },
      pageNumber: +state.searchParams.pn!,
      numberOfMails: state.searchParams.itemsPerPage,
      numberOfFiles: 3,
      includeFilters: searchParams?.sm || [],
      formIds: searchParams?.sf || [],
      labelIds: searchParams?.l || [],
      getSubUserSentMails: searchParams?.rcp?.length
        ? searchParams?.rcp
        : (["my"] as SubUserSentMailsT[]),
      mailActionStatus: searchParams?.sa || [],
      subUserEmails: searchParams?.su || [],
    };

    const response = await ReactGetSentMailsApiRequest(
      payload,
      abortController
    );

    if (response.status === "success") {
      if (window.innerWidth < 992 && +state.searchParams.pn! > 1) {
        dispatch(
          setMailsData({ ...state.mailsData, ...response.data?.mailsData })
        );
      } else {
        dispatch(setMailsData(response.data!.mailsData));
      }

      dispatch(setMailsQuantity(response.data!.mailsQuantity));
      dispatch(setSelectedMailIds([]));
      dispatch(setApiEnable(response.data!.APIEnable));
      dispatch(setFtpEnable(response.data!.FTPEnable));
      dispatch(setIsFetching(false));

      await dispatch(fetchMailsCount());
    } else {
      dispatch(setMailsData({}));
      if ((response.data as unknown as string) === "user-not-confirmed") {
        const link =
          window.location.href.substring(
            0,
            window.location.href.lastIndexOf("/") + 1
          ) + "registration-complete/incomplete";
        window.location.href = link;
      }
    }
    dispatch(setIsFetching(false));
  }
);

export const fetchMailsCount = createAsyncThunk(
  "inbox/fetchMailsCount",
  async (_, { getState, dispatch }) => {
    const state = (getState() as RootState).inbox;

    const tParam = state.searchParams.t;
    const searchParams = state.searchParams[
      tParam
    ] as (typeof state.searchParams)["all"];

    const payload = {
      filterGlobal: tParam,
      getSubUserSentMails: searchParams?.rcp?.length
        ? searchParams.rcp
        : (["my"] as SubUserSentMailsT[]),
      filterType: state.searchParams.f,
      labelIds: searchParams?.l || [],
      subUserEmails: searchParams?.su || [],
    };

    const response = await ReactGetSentMailsCountApiRequest(payload);
    if (response.status === "success") {
      dispatch(
        setMailsCount({
          ...state.mailsCount,
          ...response.data,
          received: response.data!.inbox,
          sent: response.data!.outbox,
        })
      );
    } else {
      console.error("InboxFactory", "ReactGetSentMailsCount", response.data);
    }
  }
);

export const updateMailSecuritySettings = createAsyncThunk(
  "inbox/updateMailSecuritySettings",
  async (
    {
      body,
      mailId,
      callback,
      setSecurity,
    }: {
      body: any;
      mailId: string;
      callback: () => void;
      setSecurity: (security: MailDataI["security"]) => void;
    },
    { dispatch }
  ) => {
    const requestBody = {
      mailId: mailId,
      downloadsLimit: body.DownloadLimit,
      password: body.SecurityPass,
      isBlockShare: body.BlockShare,
      recipientsOnly: body.RecipientAccess,
      previewFiles: body.PreviewAccess,
      collaboration: body.Collaboration,
      recaptchaResponse: body.recaptchaResponse,
      // captchaServiceName: "cloudflare",
    };

    const response = await ReactUpdateSecuritySettingsApiRequest(requestBody);

    if (response.status === "success") {
      const checkIfSecurityDisabled = () => {
        return Object.keys(requestBody).every((item) => {
          if (["userId", "mailId"].includes(item)) {
            return true;
          }
          if (item === "password") {
            return requestBody[item]?.length === 0;
          }
          return !requestBody[item as keyof typeof requestBody];
        });
      };

      const newSecurityObj = checkIfSecurityDisabled()
        ? null
        : {
            downloadLimit: requestBody.downloadsLimit,
            password: requestBody.password,
            isSharingDisabled: requestBody.isBlockShare,
            isPasswordProtected: requestBody.password ? true : false,
            previewFiles: requestBody.previewFiles,
            recipientsOnly: requestBody.recipientsOnly,
            collaboration: requestBody.collaboration,
          };

      if (window.location.href.includes("inbox")) {
        dispatch(
          setMailDataSecurity({
            mailId,
            security: newSecurityObj,
          })
        );
      } else {
        setSecurity(newSecurityObj);
      }
      callback();
    }

    return response;
  }
);

export const updateMailPayments = createAsyncThunk(
  "inbox/updateMailPayments",
  async (
    {
      body,
      mailId,
      callback,
    }: {
      body: {
        PaymentAmount: number;
        DisplayName: string;
        PaymentOnce: boolean;
        PreviewItems: boolean;
        recaptchaResponse: string;
      };
      mailId: string;
      callback: () => void;
    },
    { dispatch, getState }
  ) => {
    const state = (getState() as RootState).inbox;

    const requestBody = {
      mailId: mailId,
      paymentSum: body.PaymentAmount,
      displayName: body.DisplayName,
      paymentCurrency: "nis",
      paymentOnes: body.PaymentOnce,
      denyPreview: body.PreviewItems,
      recaptchaResponse: body.recaptchaResponse,
    };

    const response = await ReactUpdatePaymentSettingsApiRequest(requestBody);

    if (response.status === "success") {
      dispatch(
        setMailData({
          mailId,
          mailData: {
            ...state.mailsData[mailId],
            payment:
              parseInt(requestBody.paymentSum as unknown as string) > 0
                ? {
                    PaymentType: requestBody.paymentOnes
                      ? "OnceForAllUsers"
                      : "perUser",
                    DisplayName: requestBody.displayName,
                    Price: requestBody.paymentSum,
                    DenyPreview: requestBody.denyPreview,
                  }
                : null,
          },
        })
      );
      callback();
    }

    return response;
  }
);

export interface RemoveMailsActionPayloadI {
  mailsArray: string[];
  mailStatus?: string;
  showToast?: (status: string[]) => void;
  setLoadingStatus?: (status: boolean) => void;
  withSavedByOtherUser?: boolean;
  isUndo?: boolean;
  userId: string;
}

export const removeMailsAction = createAsyncThunk(
  "inbox/removeMailsAction",
  async (
    {
      mailsArray,
      mailStatus,
      showToast,
      setLoadingStatus,
      withSavedByOtherUser,
      isUndo: isUndoProp,
      userId,
    }: RemoveMailsActionPayloadI,
    { getState, dispatch }
  ) => {
    const state = (getState() as RootState).inbox;

    if (setLoadingStatus) {
      setLoadingStatus(true);
    }
    const isUndo = mailStatus === "pendingRemoved" || isUndoProp;

    const mailsArrayWithoutSavedByOtherUsers = mailsArray.filter(
      (mailId) => !state.mailsData?.[mailId]?.isSavedByOtherUser
    );
    const mailList =
      withSavedByOtherUser || isUndo
        ? mailsArray
        : mailsArrayWithoutSavedByOtherUsers;
    const mailsRemovingStatus = getMailsRemovingStatus(
      state.mailsData,
      mailsArray
    );

    const response = await ReactRemoveInboxMailsApiRequest(mailList, !!isUndo);

    if (response.status === "success") {
      if (!isUndo) {
        mailList.forEach((mailId) => {
          const removingStatus = mailsRemovingStatus[mailId];

          if (removingStatus === "remove") {
            dispatch(removeMailFromState(mailId));
          } else if (removingStatus === "status change") {
            dispatch(
              setMailData({
                mailId,
                mailData: {
                  ...state.mailsData[mailId],
                  isSavedByUser: false,
                  mailStatus:
                    response.data[mailId] === "sent"
                      ? "active"
                      : response.data[mailId],
                },
              })
            );
          }
        });

        const isAllMailsRemoved = Object.keys(mailsRemovingStatus).every(
          (mailId) => mailsRemovingStatus[mailId] === "remove"
        );
        const totalMails = Object.keys(state.mailsData).length;

        if (
          totalMails === Object.keys(mailsRemovingStatus).length &&
          isAllMailsRemoved
        ) {
          const currentPage = +state.searchParams.pn!;
          const prevPage = currentPage - 1;
          if (currentPage === 1) {
            dispatch(fetchInboxData(userId));
          } else {
            dispatch(setCurrentPage(prevPage));
          }
        }
      } else {
        dispatch(fetchInboxData(userId));
      }
      if (setLoadingStatus) {
        setLoadingStatus(false);
      }
    } else {
      if (setLoadingStatus) {
        setLoadingStatus(false);
      }
    }
    dispatch(fetchMailsCount());
    showToast?.(Object.values(mailsRemovingStatus));
    return response;
  }
);

export interface RemoveMailForeverPayloadI {
  setLoadingStatus?: (status: boolean) => void;
  encryptedMailIds: string[];
  userId: string;
}

export const removeMailForever = createAsyncThunk(
  "inbox/removeMailForever",
  async (
    { setLoadingStatus, encryptedMailIds, userId }: RemoveMailForeverPayloadI,
    { getState, dispatch }
  ) => {
    setLoadingStatus?.(true);
    const response = await RemoveMailFromTrashApiRequest(encryptedMailIds);
    setLoadingStatus?.(false);

    if (response?.status === "success") {
      const state = (getState() as RootState).inbox;
      const totalMails = Object.keys(state.mailsData).length;

      if (totalMails === encryptedMailIds.length) {
        dispatch(fetchInboxData(userId));
        dispatch(fetchMailsCount());
      }

      for (const mailId of encryptedMailIds) {
        dispatch(removeMailFromState(mailId));
      }
    }

    return response;
  }
);

export const fetchSideInboxFiles = createAsyncThunk(
  "inbox/fetchSideInboxFiles",
  async (
    {
      encryptedMailId,
      numberOfFiles,
    }: { encryptedMailId: string; numberOfFiles: number },
    { getState, dispatch }
  ) => {
    const state = (getState() as RootState).inbox;

    if (state.isFetchingSideInboxFiles) return;

    if (state.sideInboxFiles?.[encryptedMailId]) return;

    dispatch(setIsFetchingSideInboxFiles(true));

    const payload = {
      encryptedMailId,
      numberOfFiles,
    };

    const response = await ReactGetSideInboxFilesApiRequest(payload);

    if (response.status === "success") {
      dispatch(
        setSideInboxFiles({
          ...state.sideInboxFiles,
          [encryptedMailId]: response.data,
        })
      );
    } else {
      console.error("ContentFactory", "fetchMetaData", response.data);
      dispatch(setSideInboxFiles({}));
    }
    dispatch(setIsFetchingSideInboxFiles(false));
  }
);

export const fetchFormList = createAsyncThunk(
  "inbox/fetchFormList",
  async (_, { dispatch }) => {
    const response = await GetAllEmbedDynamicFormsNamesByUserIdApiRequest();

    if (response.status === "success") {
      dispatch(setFormList(response.data));
    } else {
      console.error("InboxFactory", "fetchFormList", response.data);
    }
    return response;
  }
);

export const fetchLabels = createAsyncThunk(
  "inbox/fetchLabels",
  async (_, { dispatch }) => {
    const response = await GetMailLabelsApiRequest();
    if (response.status === "success" && Array.isArray(response.data)) {
      for (const [index, label] of (response.data as any).entries()) {
        dispatch(
          setLabel({
            id: label.id,
            name: label.name,
            color: label.color || getCustomStatusColor(index),
          })
        );
      }
    } else {
      console.error("InboxFactory", "Labels", response.data);
    }
    return response;
  }
);

export const createLabel = createAsyncThunk(
  "inbox/createLabel",
  async ({ name, color }: { name: string; color: string }, { dispatch }) => {
    const response = await CreateLabelApiRequest(name, color);

    if (response.status === "success") {
      dispatch(
        setLabel({
          id: response.data.id,
          name: response.data.name,
          color: response.data.color,
        })
      );
    } else {
      console.error("InboxFactory", "CreateLabel", response.data);
    }

    return response;
  }
);

export const updateLabel = createAsyncThunk(
  "inbox/updateLabel",
  async (data: { id: number; name: string; color: string }, { dispatch }) => {
    const response = await UpdateLabelApiRequest({
      id: data.id.toString(),
      name: data.name,
      color: data.color,
    });

    if (response.status === "success") {
      dispatch(
        setLabel({
          id: response.data.id,
          name: response.data.name,
          color: response.data.color,
        })
      );
    } else {
      console.error("InboxFactory", "UpdateLabel", response.data);
    }

    return response;
  }
);

export const deleteLabel = createAsyncThunk(
  "inbox/deleteLabel",
  async ({ id }: { id: number }, { getState, dispatch }) => {
    const response = await DeleteLabelApiRequest(id + "");

    if (response.status === "success") {
      const state = (getState() as RootState).inbox;

      dispatch(removeLabel(id));

      // Clear all label data from all mails
      Object.keys(state.mailsData || []).forEach((mailId) => {
        if (state.mailsData[mailId]?.labelIds?.includes(id)) {
          dispatch(
            setMailData({
              mailId,
              mailData: {
                ...state.mailsData[mailId],
                labelIds: state.mailsData[mailId].labelIds.filter(
                  (labelId) => labelId !== id
                ),
              },
            })
          );
        }
      });
    } else {
      console.error("InboxFactory", "DeleteLabel", response.data);
    }

    return response;
  }
);

export const labelMails = createAsyncThunk(
  "inbox/labelMails",
  async (
    {
      encryptedMailIds,
      labelIds,
    }: { encryptedMailIds: string[]; labelIds: number[] },
    { getState, dispatch }
  ) => {
    const state = (getState() as RootState).inbox;

    const _updateLabelsCounters = (labelIds: number[]) => {
      labelIds.forEach((labelId) => {
        if (state.mailsCount?.labels) {
          if (state.mailsCount.labels.hasOwnProperty(labelId)) {
            dispatch(
              setMailsCount({
                ...state.mailsCount,
                labels: {
                  ...state.mailsCount.labels,
                  [labelId]:
                    state.mailsCount.labels[labelId as unknown as number] + 1,
                },
              })
            );
          } else {
            dispatch(
              setMailsCount({
                ...state.mailsCount,
                labels: {
                  ...state.mailsCount.labels,
                  [labelId]: 1,
                },
              })
            );
          }
        }
      });
    };

    const response = await LabelMailsApiRequest(encryptedMailIds, labelIds);

    if (response.status === "success") {
      Object.keys(response.data)?.forEach((mailId) => {
        const mail = state.mailsData[mailId];

        if (mail) {
          const uniqueLabelIds = labelIds.filter(
            (labelId) => !(mail.labelIds || []).includes(labelId)
          );

          // Assuming updateLabelsCounters is an action or function you have defined elsewhere
          _updateLabelsCounters(uniqueLabelIds);
        }

        dispatch(
          setMailData({
            mailId,
            mailData: {
              ...state.mailsData[mailId],
              labelIds: response.data[mailId],
            },
          })
        );
      });

      return response;
    } else {
      console.error("InboxFactory", "labelMails", response.data);
      return response;
    }
  }
);

export const unlabelMails = createAsyncThunk(
  "inbox/unlabelMails",
  async (
    {
      encryptedMailIds,
      labelIds,
    }: { encryptedMailIds: string[]; labelIds: number[] },
    { getState, dispatch }
  ) => {
    const state = (getState() as RootState).inbox;

    const response = await UnlabelMailsApiRequest(encryptedMailIds, labelIds);

    if (response.status === "success") {
      Object.keys(response.data)?.forEach((mailId) => {
        dispatch(
          setMailData({
            mailId,
            mailData: {
              ...state.mailsData[mailId],
              labelIds: response.data[mailId],
            },
          })
        );
      });

      labelIds.forEach((labelId) => {
        dispatch(
          setMailsCount({
            ...state.mailsCount,
            labels: {
              ...state.mailsCount?.labels,
              [labelId]: state.mailsCount!.labels![labelId] - 1,
            },
          })
        );
      });

      return response;
    } else {
      console.error("InboxFactory", "unlabelMails", response.data);
      return response;
    }
  }
);
export const fetchSubUserList = createAsyncThunk(
  "inbox/fetchSubUserList",
  async (_, { dispatch }) => {
    const response = await GetSubUserDetailsApiRequest();
    if (response.status === "success") {
      dispatch(setSubUserList(response.data));
    } else {
      console.error("InboxFactory", "fetchSubUserList", response.data);
    }

    return response;
  }
);
